import React from 'react';
import { FileText, Upload, Eye, Download, Trash2, X, Loader2 } from 'lucide-react';
import type { Customer, MultiFondationDocument } from '../../types';

export interface CustomerDocumentsTabProps {
  formData: Partial<Customer>;
  data: Customer;
  isUploadPending: boolean;
  isDeletePending: boolean;
  getFileUrl: (filePath: string | undefined) => string;
  onFileUpload: (field: string, file: File) => void;
  onFileDelete: (field: string, documentName: string) => void;
  showAddDocumentModal: 'contrat_regroupement' | 'instruction' | null;
  onShowAddDocumentModal: (type: 'contrat_regroupement' | 'instruction' | null) => void;
  newDocumentFondation: string;
  onNewDocumentFondationChange: (value: string) => void;
  newDocumentFile: File | null;
  onNewDocumentFileChange: (file: File | null) => void;
  onAddMultiFondationDocument: () => void;
  isLoadingFondations: boolean;
  availableFondations: string[];
}

const CustomerDocumentsTab: React.FC<CustomerDocumentsTabProps> = ({
  formData,
  data,
  isUploadPending,
  isDeletePending,
  getFileUrl,
  onFileUpload,
  onFileDelete,
  showAddDocumentModal,
  onShowAddDocumentModal,
  newDocumentFondation,
  onNewDocumentFondationChange,
  newDocumentFile,
  onNewDocumentFileChange,
  onAddMultiFondationDocument,
  isLoadingFondations,
  availableFondations,
}) => {
  const renderDocumentCard = (doc: { label: string; field: string; icon: typeof FileText }) => {
    const Icon = doc.icon;
    const hasDocument = formData[doc.field as keyof Customer];
    return (
      <div
        key={doc.field}
        className={`relative border-2 rounded-xl p-5 transition-all duration-200 ${
          hasDocument
            ? 'border-green-200 bg-green-50 hover:shadow-md'
            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${hasDocument ? 'bg-green-100' : 'bg-gray-200'}`}>
              <Icon className={`h-5 w-5 ${hasDocument ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">{doc.label}</h4>
              <p className={`text-xs mt-1 ${hasDocument ? 'text-green-600' : 'text-red-600'}`}>
                {hasDocument ? 'Disponible' : 'Non fourni'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {hasDocument ? (
            <>
              <a
                href={getFileUrl(formData[doc.field as keyof Customer] as string)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Eye className="h-3.5 w-3.5" />
                Voir
              </a>
              <a
                href={getFileUrl(formData[doc.field as keyof Customer] as string)}
                download
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                {`T\u00e9l\u00e9charger`}
              </a>
              <button
                onClick={() => onFileDelete(doc.field, doc.label)}
                disabled={isDeletePending}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </>
          ) : (
            <label className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors cursor-pointer">
              <Upload className="h-3.5 w-3.5" />
              {isUploadPending ? `T\u00e9l\u00e9versement...` : `T\u00e9l\u00e9verser`}
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onFileUpload(doc.field, file);
                    e.target.value = ''; // Reset input
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png"
                disabled={isUploadPending}
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  const renderMultiFondationDoc = (doc: MultiFondationDocument, typePrefix: string, typeLabel: string) => (
    <div
      key={doc.id}
      className="relative border-2 rounded-xl p-5 transition-all duration-200 border-green-200 bg-green-50 hover:shadow-md"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100">
            <FileText className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">{typeLabel}</h4>
            <p className="text-xs mt-1 text-green-600">{doc.fondation_name}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <a
          href={getFileUrl(doc.file_path)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          <Eye className="h-3.5 w-3.5" />
          Voir
        </a>
        <a
          href={getFileUrl(doc.file_path)}
          download
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          {`T\u00e9l\u00e9charger`}
        </a>
        <button
          onClick={() => onFileDelete(`${typePrefix}:${doc.fondation_name}`, `${typeLabel} - ${doc.fondation_name}`)}
          disabled={isDeletePending}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Supprimer
        </button>
      </div>
    </div>
  );

  const contractualDocs = [
    { label: 'Procuration', field: 'procuration', icon: FileText },
    { label: 'Lettre de libre passage', field: 'libre_passage', icon: FileText },
    { label: "Pi\u00e8ce d'identit\u00e9 recto", field: 'photo', icon: FileText },
    { label: "Pi\u00e8ce d'identit\u00e9 verso", field: 'photo2', icon: FileText },
  ];

  const institutionalDocs = [
    { label: 'Demande suppl\u00e9tive', field: 'demande_suppletive', icon: FileText },
    { label: 'R\u00e9ponse suppl\u00e9tive', field: 'reponse_suppletive', icon: FileText },
    { label: 'R\u00e9ponse centrale', field: 'reponse', icon: FileText },
  ];

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Documents contractuels</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {contractualDocs.map(renderDocumentCard)}
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-6 mt-10">Documents institutionnels</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {institutionalDocs.map(renderDocumentCard)}
        </div>

        {/* Multi-fondation Documents: Contrats de regroupement */}
        <div className="flex items-center justify-between mb-6 mt-10">
          <h3 className="text-xl font-semibold text-gray-900">Contrats de regroupement par fondation</h3>
          <button
            onClick={() => onShowAddDocumentModal('contrat_regroupement')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Ajouter
          </button>
        </div>
        {data.contrat_regroupement_documents && data.contrat_regroupement_documents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.contrat_regroupement_documents.map((doc: MultiFondationDocument) =>
              renderMultiFondationDoc(doc, 'contrat_regroupement', 'Contrat regroupement')
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            Aucun contrat de regroupement
          </div>
        )}

        {/* Multi-fondation Documents: Instructions */}
        <div className="flex items-center justify-between mb-6 mt-10">
          <h3 className="text-xl font-semibold text-gray-900">Instructions par fondation</h3>
          <button
            onClick={() => onShowAddDocumentModal('instruction')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Ajouter
          </button>
        </div>
        {data.instruction_documents && data.instruction_documents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.instruction_documents.map((doc: MultiFondationDocument) =>
              renderMultiFondationDoc(doc, 'instruction', 'Instruction')
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            Aucune instruction
          </div>
        )}
      </div>

      {/* Add Multi-fondation Document Modal */}
      {showAddDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  Ajouter {showAddDocumentModal === 'contrat_regroupement' ? 'un contrat de regroupement' : 'une instruction'}
                </h3>
                <button
                  onClick={() => {
                    onShowAddDocumentModal(null);
                    onNewDocumentFondationChange('');
                    onNewDocumentFileChange(null);
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Fondation Select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fondation
                </label>
                {isLoadingFondations ? (
                  <div className="flex items-center justify-center py-3 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Chargement des fondations...
                  </div>
                ) : availableFondations.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                    {`Toutes les fondations ont d\u00e9j\u00e0 un document de ce type.`}
                  </div>
                ) : (
                  <select
                    value={newDocumentFondation}
                    onChange={(e) => onNewDocumentFondationChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                  >
                    <option value="">{`S\u00e9lectionner une fondation`}</option>
                    {availableFondations.map((fondation: string) => (
                      <option key={fondation} value={fondation}>
                        {fondation}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 transition-all ${
                    newDocumentFile
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50'
                  }`}
                >
                  <input
                    type="file"
                    onChange={(e) => onNewDocumentFileChange(e.target.files?.[0] || null)}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  {newDocumentFile ? (
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {newDocumentFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(newDocumentFile.size / 1024).toFixed(1)} Ko
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNewDocumentFileChange(null);
                        }}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto h-10 w-10 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium text-primary-600">{`Cliquez pour s\u00e9lectionner`}</span>
                        {' '}ou glissez-d\u00e9posez
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        PDF, JPG ou PNG (max. 10 Mo)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => {
                  onShowAddDocumentModal(null);
                  onNewDocumentFondationChange('');
                  onNewDocumentFileChange(null);
                }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={onAddMultiFondationDocument}
                disabled={!newDocumentFondation || !newDocumentFile || isUploadPending || availableFondations.length === 0}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUploadPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Ajouter
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerDocumentsTab;
